import { FC } from 'react';
import {
    Clock,
    MapPin,
    ChevronLeft,
    ChevronRight,
    Globe,
    Instagram,
    Youtube,
    Linkedin,
    Hourglass
} from 'lucide-react';
import { getBookingStyles } from './BookingStyles';
import { BookingInfo } from './BookingTypes';

interface BookingFormProps {
    info: BookingInfo;
    selectedDate: string;
    setSelectedDate: (date: string) => void;
    selectedTime: string;
    setSelectedTime: (time: string) => void;
    availableSlots: any[];
    fetchingAvailability: boolean;
    currentMonth: Date;
    setCurrentMonth: (date: Date) => void;
    submitting: boolean;
    onSubmit: () => void;
}

const BookingForm: FC<BookingFormProps> = ({
    info,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    availableSlots,
    fetchingAvailability,
    currentMonth,
    setCurrentMonth,
    submitting,
    onSubmit
}) => {
    // Forced App Theme is handled in CSS

    const renderCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];
        const todayStr = new Date().toISOString().split('T')[0];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const isPast = dateStr < todayStr;
            const isSelected = selectedDate === dateStr;

            days.push(
                <div
                    key={d}
                    className={`calendar-day ${isPast ? 'past' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => !isPast && setSelectedDate(dateStr)}
                >
                    {d}
                </div>
            );
        }
        return days;
    };

    const renderTimeSlots = () => {
        if (!selectedDate) return <div className="p-4 text-center text-gray-400">Select a date to view available times.</div>;
        if (fetchingAvailability) return <div className="p-4 text-center text-gray-400">Checking availability...</div>;
        if (availableSlots.length === 0) return <div className="p-4 text-center text-gray-400">No time slots found for this day.</div>;

        return (
            <div className="slots-grid-container animate-up">
                {availableSlots.map((slot, index) => {
                    const timeStr = new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                    const isBusy = slot.status === 'busy';
                    return (
                        <div
                            key={index}
                            className={`time-slot ${isBusy ? 'busy' : ''} ${selectedTime === slot.start ? 'selected' : ''}`}
                            onClick={() => !isBusy && setSelectedTime(slot.start)}
                        >
                            {timeStr}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="booking-page-root">
            <style>{getBookingStyles()}</style>

            <div className="portal-container">
                <div className="brand-sidebar">
                    <div className="brand-header">
                        {info.company.logoUrl ? (
                            <img src={info.company.logoUrl} alt={info.company.name} className="brand-logo" />
                        ) : (
                            <h2 className="brand-name-text">{info.company.name}</h2>
                        )}
                    </div>

                    <div className="sidebar-profile">
                        <div className="avatar-container">
                            <img src={info.user.avatar || 'https://via.placeholder.com/120'} alt="Profile" className="avatar-img" />
                        </div>
                        <h2 className="profile-name">{info.user.name}</h2>
                        <p className="profile-company">{info.company.name}</p>
                        <div className="profile-bio">{info.user.bio}</div>
                    </div>

                    <div className="sidebar-info-list">
                        <div className="sidebar-info-item"><Hourglass size={16} /> <span>{info.config.slotDuration} Minute Meeting</span></div>
                        <div className="sidebar-info-item"><Clock size={16} /> <span>Mon-Fri {info.config.businessHoursStart} - {info.config.businessHoursEnd}</span></div>
                        <div className="sidebar-info-item"><MapPin size={16} /> <span>{info.user.city || 'Worldwide'}, {info.user.country || 'Remote'}</span></div>
                        <div className="sidebar-info-item"><Globe size={16} /> <span>{info.user.timezone || 'UTC'} Time</span></div>
                    </div>

                    <div className="sidebar-social">
                        <Linkedin size={18} className="social-icon" />
                        <Instagram size={18} className="social-icon" />
                        <Youtube size={18} className="social-icon" />
                    </div>
                </div>

                <div className="scheduling-content">
                    <div className="content-header">
                        <h1 className="main-title">Book a meeting</h1>
                        <p className="main-subtitle">Select a date and time that works for you.</p>
                    </div>

                    <div className="scheduling-body">
                        <div className="calendar-section">
                            <span className="section-title">Select Date</span>
                            <div className="card-header">
                                <button className="nav-btn" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}><ChevronLeft size={16} /></button>
                                <span className="current-month">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                <button className="nav-btn" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}><ChevronRight size={16} /></button>
                            </div>
                            <div className="dow-grid">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="dow-label">{day}</div>)}</div>
                            <div className="days-grid">{renderCalendarDays()}</div>
                        </div>

                        {selectedDate && (
                            <div className="time-section animate-up">
                                <span className="section-title">Select Time for {new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                {renderTimeSlots()}
                            </div>
                        )}
                    </div>

                    <div className="footer-action">
                        {selectedDate && selectedTime ? (
                            <button className="confirm-btn" onClick={onSubmit} disabled={submitting}>
                                {submitting ? 'Confirming...' : `Confirm Meeting at ${new Date(selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`}
                            </button>
                        ) : (
                            <div className="selection-hint">{!selectedDate ? 'Please select a date' : 'Now select a time slot'}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="page-footer">Secured by <strong>Carion Portal</strong></div>
        </div>
    );
};

export default BookingForm;
